import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { isBillingEnabled } from '@/api/config'
import { NAV_GROUPS } from '@/domains/app-shell'
import { useUser } from '@/domains/auth'
import { useSetupState } from '@/domains/setup'

import type { CommandPaletteSection } from './types'
import { useRegisterPaletteSections } from './use-register-palette-sections'

/** registers every sidebar entry as a navigation row in the command palette.
 * the catalogue is the sidebar's NAV_GROUPS (the single source of truth for
 * what the user can navigate to), with super-only groups filtered out for
 * non-super users so the palette mirrors the sidebar's visibility rules.
 *
 * each top-level NavItem and each child becomes its own palette row,
 * because the palette is search-first and a flat list narrows faster than
 * nested groups do. parent rows that just collapse children in the sidebar
 * still navigate in the palette (their `href` is the parent's landing
 * route).
 */
export function useRegisterAppPages() {
  const navigate = useNavigate()
  const { user } = useUser()
  const { setupState } = useSetupState()
  /** mirrors the sidebar exactly. gating on isSuper alone would offer company
   * pages in Super scope, and those routes are company-scoped, so they 403. */
  const inSuperScope = (user?.isSuper ?? false) && setupState?.scope === 'super'
  const isSupplier = setupState?.isSupplier ?? false
  const billingEnabled = isBillingEnabled()

  const sections = useMemo<CommandPaletteSection[]>(() => {
    const out: CommandPaletteSection[] = []
    for (const group of NAV_GROUPS) {
      const superOnly = group.superOnly === true
      if (inSuperScope !== superOnly) continue
      for (const item of group.items) {
        if (item.supplierOnly && !isSupplier) continue
        if (item.billingOnly && !billingEnabled) continue
        out.push({
          id: `page:${item.href}`,
          title: item.label,
          icon: item.icon,
          group: 'Pages',
          onSelect: () => void navigate(item.href)
        })
        if (item.children) {
          for (const child of item.children) {
            // child id includes the parent href so e.g. /suppliers (parent) and /suppliers (all suppliers child) don't collide
            out.push({
              id: `page:${item.href}:${child.href}`,
              title: child.label,
              icon: child.icon,
              group: 'Pages',
              onSelect: () => void navigate(child.href)
            })
          }
        }
      }
    }
    return out
  }, [billingEnabled, inSuperScope, isSupplier, navigate])

  useRegisterPaletteSections(sections)
}
